import { isValid, parse, parseISO } from 'date-fns';

const LEGACY_FORMAT = 'yyyy-MM-dd HH:mm';
const LEGACY_PATTERN = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/;

type TimestampLike = {
    toDate?: () => Date;
    seconds?: unknown;
    nanoseconds?: unknown;
    _seconds?: unknown;
    _nanoseconds?: unknown;
};

function finiteNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
    return null;
}

function dateFromSeconds(seconds: number, nanos = 0): Date | null {
    const date = new Date(seconds * 1000 + nanos / 1e6);
    return isValid(date) ? date : null;
}

function timestampNumber(value: TimestampLike, name: 'seconds' | 'nanoseconds'): number | null {
    const record = value as Record<string, unknown>;
    return finiteNumber(record[name]) ?? finiteNumber(record[`_${name}`]);
}

function dateFromTimestampLike(value: TimestampLike): Date | null {
    if (typeof value.toDate === 'function') {
        const date = value.toDate();
        return date instanceof Date && isValid(date) ? date : null;
    }
    const seconds = timestampNumber(value, 'seconds');
    if (seconds == null) return null;
    return dateFromSeconds(seconds, timestampNumber(value, 'nanoseconds') ?? 0);
}

function dateFromSerializedTimestamp(value: string): Date | null {
    if (!/timestamp|_seconds|nanoseconds/i.test(value)) return null;
    const secondsMatch = value.match(/(?:_seconds|seconds)\s*[:=]\s*(-?\d+)/i);
    if (!secondsMatch) return null;
    const nanosMatch = value.match(/(?:_nanoseconds|nanoseconds)\s*[:=]\s*(-?\d+)/i);
    return dateFromSeconds(Number(secondsMatch[1]), nanosMatch ? Number(nanosMatch[1]) : 0);
}

/**
 * Job appointment strings are stored two ways: legacy `yyyy-MM-dd HH:mm` and
 * ISO from createShopJob/createIntake (`appointment.toDate().toISOString()`),
 * plus Firestore Timestamp shapes. Returns null instead of an Invalid Date.
 */
export function parseJobDate(dateValue: unknown): Date | null {
    try {
        if (dateValue == null || dateValue === '') return null;
        if (dateValue instanceof Date) return isValid(dateValue) ? dateValue : null;
        if (typeof dateValue === 'object') return dateFromTimestampLike(dateValue as TimestampLike);
        if (typeof dateValue !== 'string') return null;

        const trimmed = dateValue.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
                const parsed = JSON.parse(trimmed) as unknown;
                if (parsed && typeof parsed === 'object') {
                    const fromJson = dateFromTimestampLike(parsed as TimestampLike);
                    if (fromJson) return fromJson;
                }
            } catch {
                // Not JSON. Fall through to the text timestamp form.
            }
        }

        const serialized = dateFromSerializedTimestamp(trimmed);
        if (serialized) return serialized;

        if (LEGACY_PATTERN.test(trimmed)) {
            const legacy = parse(trimmed, LEGACY_FORMAT, new Date());
            return isValid(legacy) ? legacy : null;
        }

        const iso = parseISO(trimmed);
        if (isValid(iso)) return iso;
        if (trimmed.includes(' ') && !trimmed.includes('T')) {
            const swapped = parseISO(trimmed.replace(' ', 'T'));
            if (isValid(swapped)) return swapped;
        }
        return null;
    } catch {
        return null;
    }
}

const newDate = (dateStr: string): Date => parseJobDate(dateStr) ?? new Date(NaN);

export default newDate;
